import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Mail, Send, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUSD = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * /admin/auto-statements — Auto-Statement Sweep (Blueprint Decision #27).
 *
 * Surfaces the daily sweep that finds claims with patient_balance > 0
 * + no recent statement, and emails the patient + family payor with a
 * "View & pay" link. Admin can trigger the sweep manually and review
 * the send log.
 */
export default function AdminAutoStatements() {
  const { getAuthHeader } = useAuth();
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const [lastSweep, setLastSweep] = useState(null);

  const loadLog = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/billing/auto-statements/log`, getAuthHeader());
      setLog(data.log || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load statement log');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { loadLog(); }, [loadLog]);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data } = await axios.post(`${API}/billing/auto-statements/sweep`, {}, getAuthHeader());
      setLastSweep(data);
      toast.success(`Swept ${data.candidates} claims · sent ${data.sent}`);
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sweep failed');
    } finally {
      setSweeping(false);
    }
  };

  return (
    <DashboardShell data-testid="auto-statements-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="cn-eyebrow inline-flex items-center gap-1.5">
            <Mail className="w-3 h-3" /> Revenue Cycle · Auto-statements
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Family statement sweep</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Finds every closed claim with an outstanding patient balance + no statement in the last 14 days,
            and emails the patient + family payor with a secure "View & pay" link. Industry-standard dunning cadence.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadLog} disabled={loading} data-testid="log-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={runSweep} disabled={sweeping} className="bg-cn-primary text-white hover:bg-cn-primary-hover" data-testid="sweep-btn">
            <Send className="w-4 h-4 mr-2" /> {sweeping ? 'Sweeping…' : 'Run sweep now'}
          </Button>
        </div>
      </header>

      {/* LAST SWEEP RESULT */}
      {lastSweep && (
        <div className="rounded-2xl bg-cn-primary/5 border border-cn-primary/20 p-5 mb-6" data-testid="sweep-result">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cn-primary mb-2">
            <CheckCircle2 className="w-3 h-3" /> Last sweep result
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Tile label="Candidates" value={lastSweep.candidates} />
            <Tile label="Sent" value={lastSweep.sent} tone="positive" />
            <Tile label="Skipped (cooldown)" value={lastSweep.skipped} />
            <Tile label="Failed" value={lastSweep.failed} tone={lastSweep.failed > 0 ? 'negative' : 'neutral'} />
          </div>
        </div>
      )}

      {/* SEND LOG */}
      <section className="rounded-2xl bg-white border border-slate-100 overflow-hidden" data-testid="statement-log">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Send log</h2>
          <span className="text-xs text-slate-500">{log.length} entries</span>
        </div>
        {loading ? (
          <div className="p-8 text-slate-500">Loading log…</div>
        ) : log.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-semibold">No statements sent yet.</p>
            <p className="text-sm text-slate-500 mt-1">Click "Run sweep now" to send the first batch.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left p-3 font-semibold">Sent at</th>
                <th className="text-left p-3 font-semibold">To</th>
                <th className="text-left p-3 font-semibold">Claim</th>
                <th className="text-right p-3 font-semibold">Balance</th>
                <th className="text-left p-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {log.slice(0, 100).map((e) => (
                <tr key={e.id} className="border-b border-slate-100 last:border-0" data-testid={`log-row-${e.id}`}>
                  <td className="p-3 text-slate-600 text-xs">{new Date(e.sent_at).toLocaleString()}</td>
                  <td className="p-3 text-slate-700 truncate max-w-[200px]">{e.to_email}</td>
                  <td className="p-3 text-slate-700 font-mono text-xs">{(e.claim_id || '').slice(0, 8)}</td>
                  <td className="p-3 text-right tabular-nums font-semibold">{fmtUSD(e.amount_due)}</td>
                  <td className="p-3">
                    {e.sent ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> Sent
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-semibold">
                        <AlertCircle className="w-3 h-3" /> Failed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </DashboardShell>
  );
}

function Tile({ label, value, tone }) {
  const color = tone === 'positive' ? 'text-emerald-700' : tone === 'negative' ? 'text-rose-700' : 'text-slate-900';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`text-2xl font-extrabold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}
